// Analyses row state (DESIGN_SPEC §12.17): what each list row says, from the job record plus what a
// stream or behavior-report peek learned (peek.ts). Pure; times are epoch ms.
//   effectiveOf(job, read?)   → Eff      effective state (the stream beats the lagging record; a terminal
//                                        record is final), verdict (stream › record › behavior report), phase
//   livenessOf(eff, now)      → Liveness live · overdue (on a node past its timeout) · queued · null (lib/records)
//   rowView(eff, ctx)         → RowView  the state (verdict + score, or run state), signal and ran cells
//   strongestTag(tags)        → { tag, tech?, more } | null   highest severity, ties broken by hits
//   cancelPhrase(eff)         → "cancelled during boot" · "cancelled while running" · "cancelled · <reason>"
//   queuePositions(effs)      → Map<id, n>   1-based place among queued runs of the same profile
//   dayGroups(effs)           → { key, label, rows }[]   UTC days, in list order (newest first) · dayOf(eff)
//   sortNewest(jobs)          → jobs by submitted_at, newest first (stable)

import { isTerminal, verdictFromRecord } from "@/lib/eventStore";
import { clock, count, ordinal, plural, toMs, utc } from "@/lib/format";
import { liveness, type Liveness } from "@/lib/records";
import type { VerdictLabel, VerdictSnap } from "@/lib/derive/types";
import type { EffectiveState, Job, Tag } from "@/types";
import type { PeekRead, PeekStatus } from "./peek";

type Bucket = "queued" | "running" | "completed" | "failed" | "cancelled";

export type Eff = {
  id: string;
  job: Job;
  /** Effective lifecycle state (§12.1). */
  state: EffectiveState;
  bucket: Bucket;
  /** Not terminal (queued counts as live). */
  live: boolean;
  verdict: VerdictSnap | null;
  verdictFrom: "stream" | "record" | "report" | null;
  /** The last non-terminal state the stream saw (where a cancelled run stopped). */
  phase: EffectiveState | null;
  /** Reason on the latest stream state, when the stream decides the state. */
  streamReason?: string;
  /** Events the peek has seen (null: the stream was not read). */
  events: number | null;
  t0: number | null;
  /** When the run ended, from the stream (null: unknown; the record's ended_at is used instead). */
  endAt: number | null;
  /** The newest event the peek saw (event time), or null. */
  lastEventAt: number | null;
  /** Since when the stream has been ahead of the record (stream time), or null. */
  lagSince: number | null;
  peek: PeekStatus;
};

const LIVE: ReadonlySet<EffectiveState> = new Set<EffectiveState>(["dispatched", "provisioning", "booting", "running", "collecting"]);
const rank = (s: string): number => (s === "queued" ? 0 : isTerminal(s) ? 2 : 1);

function bucketOf(state: EffectiveState): Bucket {
  if (state === "queued") return "queued";
  if (LIVE.has(state)) return "running";
  return state as Bucket;
}

export function effectiveOf(job: Job, read?: PeekRead): Eff {
  const info = read?.info;
  const stream = info?.streamState ?? null;
  // a terminal record is final; otherwise the latest job.state on the stream wins
  const useStream = !!stream && !(isTerminal(job.state) && !isTerminal(stream));
  const state: EffectiveState = useStream && stream ? stream : job.state;

  let verdict: VerdictSnap | null = null;
  let verdictFrom: Eff["verdictFrom"] = null;
  if (info?.verdict) {
    verdict = info.verdict;
    verdictFrom = "stream";
  } else if (job.verdict) {
    verdict = verdictFromRecord(job.verdict);
    verdictFrom = "record";
  } else if (info?.reportVerdict) {
    verdict = info.reportVerdict;
    verdictFrom = "report";
  }

  let lagSince: number | null = null;
  if (useStream && info?.stateAt != null) {
    const r = rank(job.state);
    const s = rank(state);
    if (s > r || (s === 2 && r === 2 && state !== job.state)) lagSince = info.stateAt;
  }

  return {
    id: job.id,
    job,
    state,
    bucket: bucketOf(state),
    live: !info?.ended && !isTerminal(state),
    verdict,
    verdictFrom,
    phase: info?.phase ?? null,
    streamReason: useStream ? info?.reason : undefined,
    events: info?.events ?? null,
    t0: info?.t0 ?? null,
    endAt: info?.endAt ?? null,
    lastEventAt: info?.lastEventAt ?? null,
    lagSince,
    peek: read?.status ?? "none",
  };
}

/** The run's place in every live count (an ended stream is never live, whatever the record says). */
export function livenessOf(eff: Eff, now: number): Liveness {
  if (!eff.live) return null;
  return liveness(eff.state, eff.job, now);
}

// ---- cells ------------------------------------------------------------------------------------

/** overdue: on a node past its timeout (plus grace): LIVE without the ping, and the word "overdue". */
export type StateCell =
  | { kind: "verdict"; label: VerdictLabel; score: number; why?: string }
  | { kind: "state"; state: Bucket; note?: string; wait?: string; overdue?: true };

export type SignalCell =
  | { kind: "tag"; tag: Tag; tech?: string; more: number; tags: readonly Tag[] }
  | { kind: "quiet"; text: string; title?: string; busy?: boolean }
  | { kind: "reason"; text: string };

type RanCell = { text: string; of?: string; title: string } | null;

type RowView = { state: StateCell; score: number | null; signal: SignalCell; ran: RanCell };

type RowCtx = {
  now: number;
  /** 1-based place among queued runs of the same profile. */
  queuePos?: number;
  /** false when the fleet is known and no node offers the job's profile. */
  profileOffered?: boolean;
};

/** The record lags once the stream has been ahead of it for this long (§10.15). */
const LAG_MS = 10_000;

export function strongestTag(tags: readonly Tag[] | null | undefined): { tag: Tag; tech?: string; more: number } | null {
  if (!tags?.length) return null;
  let best = tags[0];
  for (const t of tags) {
    if (t.severity > best.severity || (t.severity === best.severity && (t.hits ?? 0) > (best.hits ?? 0))) best = t;
  }
  return { tag: best, tech: best.mitre_ids?.find((m) => !!m), more: tags.length - 1 };
}

const PHASE: Partial<Record<EffectiveState, string>> = {
  queued: "before boot",
  dispatched: "before boot",
  provisioning: "during boot",
  booting: "during boot",
  running: "while running",
  collecting: "during collection",
};

/** Where a cancelled run stopped: the stream's own reason, else its last phase, else the record. */
export function cancelPhrase(eff: Eff): string {
  const sr = eff.streamReason?.trim();
  if (sr) return /^cancel/i.test(sr) ? sr : `cancelled · ${sr}`;
  const p = eff.phase ? PHASE[eff.phase] : undefined;
  if (p) return `cancelled ${p}`;
  const rr = eff.job.reason?.trim();
  if (rr) return /^cancel/i.test(rr) ? rr : `cancelled · ${rr}`;
  return "cancelled";
}

function verdictSignal(v: VerdictSnap): SignalCell {
  const top = strongestTag(v.tags);
  if (top) return { kind: "tag", ...top, tags: v.tags };
  if (v.tagCount > 0) return { kind: "quiet", text: `${count(v.tagCount)} behavior ${plural(v.tagCount, "tag")}` };
  return { kind: "quiet", text: "no behavior rules fired" };
}

const reading = (eff: Eff) => eff.peek === "waiting" || eff.peek === "open";

function ranOf(eff: Eff, now: number): RanCell {
  const j = eff.job;
  const start = toMs(j.dispatched_at ?? "");
  if (!Number.isFinite(start)) return null;
  const timeout = Math.max(0, j.timeout_secs) * 1000;
  if (eff.live) {
    const el = now - start;
    // past an hour the run is long past any timeout (the signal cell says so); keep the clock short
    return {
      text: clock(el),
      of: el < 3_600_000 && timeout ? clock(timeout) : undefined,
      title: `dispatched ${utc(start)} UTC · timeout ${clock(timeout)}`,
    };
  }
  const end = eff.endAt ?? toMs(j.ended_at ?? "");
  if (!Number.isFinite(end) || end - start < 500) return null;
  // a stopped run whose record was closed long after its last event (cleared by hand, no node-side state): the
  // record's end is when it was cleared, so the run ends at its last event, as the report's Run row says
  const last = eff.lastEventAt;
  if (eff.endAt === null && eff.bucket !== "completed" && last !== null && last > start && end - last > CLEARED_MS) {
    return { text: clock(last - start), title: `dispatch ${utc(start)} → last event ${utc(last)} UTC · the record was closed at ${utc(end)}` };
  }
  if (eff.endAt === null && eff.bucket !== "completed" && /no node-side state|cleared/i.test(j.reason ?? "")) {
    return { text: "—", title: `the record was cleared at ${utc(end)} UTC; the run's own end is unknown` };
  }
  const source = eff.endAt !== null ? "stream" : "record";
  return { text: clock(end - start), title: `dispatch ${utc(start)} → end ${utc(end)} UTC (${source})` };
}

/** A record closed this long after the run's last event was closed by hand, not by the run. */
const CLEARED_MS = 5 * 60_000;

export function rowView(eff: Eff, ctx: RowCtx): RowView {
  const j = eff.job;
  const ran = ranOf(eff, ctx.now);
  const v = eff.verdict;
  switch (eff.bucket) {
    case "queued": {
      const wait = clock(ctx.now - toMs(j.submitted_at));
      return {
        state: { kind: "state", state: "queued", note: ctx.queuePos ? ordinal(ctx.queuePos) : undefined, wait },
        score: null,
        signal: {
          kind: "quiet",
          text: ctx.profileOffered === false ? `${j.profile} unavailable` : `waiting for a ${j.profile} slot`,
        },
        ran: null,
      };
    }
    case "running": {
      const overdue = livenessOf(eff, ctx.now) === "overdue";
      const text = overdue
        ? `past its ${clock(j.timeout_secs * 1000)} timeout`
        : eff.events !== null
          ? `${count(eff.events)} ${plural(eff.events, "event")} so far`
          : "";
      return {
        state: overdue ? { kind: "state", state: "running", overdue: true } : { kind: "state", state: "running" },
        score: null,
        signal: { kind: "quiet", text, busy: !overdue && eff.events === null ? reading(eff) : undefined },
        ran,
      };
    }
    case "completed": {
      if (v) {
        const lag = eff.lagSince !== null && ctx.now - eff.lagSince > LAG_MS;
        return {
          state: { kind: "verdict", label: v.label, score: v.score, why: lag ? `record says ${j.state}` : undefined },
          score: v.score,
          signal: verdictSignal(v),
          ran,
        };
      }
      return {
        state: { kind: "state", state: "completed" },
        score: null,
        signal: reading(eff) ? { kind: "quiet", text: "", busy: true } : { kind: "quiet", text: "no verdict recorded" },
        ran,
      };
    }
    case "failed": {
      const reason = j.reason?.trim() || eff.streamReason?.trim();
      return {
        state: { kind: "state", state: "failed" },
        score: null,
        signal: reason ? { kind: "reason", text: reason } : { kind: "quiet", text: "no reason recorded" },
        ran,
      };
    }
    case "cancelled": {
      if (!j.node_id && !j.dispatched_at) {
        return {
          state: { kind: "state", state: "cancelled" },
          score: null,
          signal: { kind: "quiet", text: "before dispatch", title: j.reason || undefined },
          ran: null,
        };
      }
      const phrase = cancelPhrase(eff);
      const title = j.reason && !phrase.includes(j.reason) ? `record: ${j.reason}` : undefined;
      if (v) {
        return {
          state: { kind: "state", state: "cancelled" },
          score: v.score,
          signal: { kind: "quiet", text: `partial · ${v.label} ${v.score} · ${phrase}`, title },
          ran,
        };
      }
      return { state: { kind: "state", state: "cancelled" }, score: null, signal: { kind: "quiet", text: phrase, title, busy: reading(eff) }, ran };
    }
  }
}

// ---- list helpers -----------------------------------------------------------------------------

export function queuePositions(effs: readonly Eff[]): Map<string, number> {
  const byProfile = new Map<string, Eff[]>();
  for (const e of effs) {
    if (e.bucket !== "queued") continue;
    const list = byProfile.get(e.job.profile) ?? [];
    list.push(e);
    byProfile.set(e.job.profile, list);
  }
  const out = new Map<string, number>();
  for (const list of byProfile.values()) {
    list.sort((a, b) => toMs(a.job.submitted_at) - toMs(b.job.submitted_at));
    list.forEach((e, i) => out.set(e.id, i + 1));
  }
  return out;
}

type DayGroup = { key: string; label: string; rows: Eff[] };

/** The UTC day a run was submitted on: { key: "2026-10-12", label: "Mon 12 Oct" }. */
export function dayOf(e: Eff): { key: string; label: string } {
  const t = toMs(e.job.submitted_at);
  return Number.isFinite(t) ? { key: new Date(t).toISOString().slice(0, 10), label: utc(t, "day") } : { key: "unknown", label: "Unknown date" };
}

export function dayGroups(effs: readonly Eff[]): DayGroup[] {
  const out: DayGroup[] = [];
  for (const e of effs) {
    const d = dayOf(e);
    let g = out[out.length - 1];
    if (!g || g.key !== d.key) {
      g = { ...d, rows: [] };
      out.push(g);
    }
    g.rows.push(e);
  }
  return out;
}

export function sortNewest(jobs: readonly Job[]): Job[] {
  const at = (j: Job) => {
    const t = toMs(j.submitted_at);
    return Number.isFinite(t) ? t : -Infinity;
  };
  return jobs
    .map((j, i) => [j, i] as const)
    .sort((a, b) => at(b[0]) - at(a[0]) || a[1] - b[1])
    .map(([j]) => j);
}

